// validator.ts — das generische, async-fähige Validierungs-Seam (PFS-729).
//
// EIN Vertrag für „darf ich weiter / ist das gültig?" — unabhängig davon, WAS
// geprüft wird. Ein Validator ist eine opake Closure: sie schließt über den
// State des Konsumenten (Feldwerte, Auswahl, Remote-Checks) und liefert ein
// Ergebnis. Der Aufrufer (Stepper, Formular, Confirm-Dialog, Route-Guard)
// inspiziert den Input NIE — dadurch ist das Seam für jeden Input-Typ generisch.
//
// Ergebnis-Konvention (bewusst schlank, kein {ok,message}-Objekt am Rückgabeort):
//   true    → gültig, weiter
//   string  → ungültig, der String IST der anzuzeigende Fehlertext
//   false   → ungültig ohne Text (still blockiert)
// Async: der Validator darf ein Promise über dasselbe zurückgeben (z.B. ein
// Server-Check). Der Runner awaited sequenziell und stoppt beim ersten Fehler.

export type ValidationResult = true | false | string;

export type Validator = () => ValidationResult | Promise<ValidationResult>;

// Normalisiertes Ergebnis für den Aufrufer (ok + optionaler Text).
export interface ValidationOutcome {
  ok: boolean;
  message?: string;
}

// normalizeResult — die schlanke Rückgabe-Konvention in ein Outcome übersetzen.
function normalizeResult(r: ValidationResult): ValidationOutcome {
  if (r === true) return { ok: true };
  return { ok: false, message: typeof r === "string" ? r : undefined };
}

// runValidator — einen einzelnen Validator ausführen (async-fähig).
export async function runValidator(validator: Validator): Promise<ValidationOutcome> {
  return normalizeResult(await validator());
}

// runValidators — mehrere Validatoren SEQUENZIELL ausführen, Stop beim ersten
// Fehler (dessen Text wird zurückgegeben). So kann ein Schritt mehrere getextete
// Checks haben (z.B. „nichts gewählt" UND async „vergeben"), ohne dass der
// Aufrufer die Reihenfolge selbst orchestriert. Leere Liste = gültig.
export async function runValidators(validators: readonly Validator[]): Promise<ValidationOutcome> {
  for (const validator of validators) {
    const outcome = normalizeResult(await validator());
    if (!outcome.ok) return outcome;
  }
  return { ok: true };
}
